/* global ANALYTICS_API_HOST, APPCUES_API_URL, CUSTOMER_API_URL, IMAGE_SERVICE_URL, IMAGE_SERVICE_CACHE_URL, RBF_API_URL, LISTSERV_API_URL */

import qs from 'qs';
import { createFetch, createFirebase } from 'next/lib/fetch';

/**
 * Create an authenticated API client
 *
 * @param {object} auth - Auth information
 * @param {string} auth.accountId - Account ID
 * @param {string} auth.jwt - Account JWT
 * @param {string} auth.token - Firebase ID token
 * @return {function} Authenticated API client
 */
export function createApiClient(auth = {}) {
  const { accountId } = auth;

  const analyticsAPI = createFetch(ANALYTICS_API_HOST, auth);
  const appcuesAPI = createFetch(`${APPCUES_API_URL}/v1`, auth);
  const customerAPI = createFetch(`${CUSTOMER_API_URL}/v1`, auth);
  const firebaseAPI = createFirebase(auth);
  const imageServiceAPI = createFetch(`${IMAGE_SERVICE_URL}/v1`, auth);
  const stepImageAPI = createFetch(`${IMAGE_SERVICE_URL}/step-image`, auth);
  const stepImageCacheAPI = createFetch(IMAGE_SERVICE_CACHE_URL, auth);
  const rbfAPI = createFetch(`${RBF_API_URL}/v2`, auth);
  const listservAPI = createFetch(`${LISTSERV_API_URL}/v1`, auth);

  return {
    /**
     * Analytics API
     */
    getAnalytics: payload =>
      analyticsAPI(`v2/query`, {
        account: false,
        method: 'POST',
        body: { account_id: accountId, ...payload },
      }),

    getOverview: () =>
      analyticsAPI(`v1/query?account_id=${accountId}&type=overview_stats`, {
        account: false,
      }),

    /**
     * Appcues API
     */
    getProfileAttributes: () => appcuesAPI(`profile_attributes`),

    getEventNames: () => appcuesAPI(`event_names`),

    getGroupAttributes: () => appcuesAPI(`group_attributes`),

    /**
     * Customer API - Flows
     */
    getFlows: () => customerAPI('flows?summary=true'),
    getFlow: id => customerAPI(`flows/${id}`),
    createFlow: data => customerAPI('flows', { method: 'POST', body: data }),
    updateFlow: (id, delta) =>
      customerAPI(`flows/${id}`, { method: 'PATCH', body: delta }),

    cloneFlow: id => customerAPI(`flows/${id}/clone`, { method: 'POST' }),
    deleteFlow: id => customerAPI(`flows/${id}`, { method: 'DELETE' }),
    archiveFlow: id => customerAPI(`flows/${id}/archive`, { method: 'POST' }),
    unarchiveFlow: id =>
      customerAPI(`flows/${id}/unarchive`, { method: 'POST' }),

    createStepGroups: (flowId, stepGroup) =>
      customerAPI(`flows/${flowId}/stepGroups`, {
        method: 'POST',
        body: stepGroup,
      }),
    createStepChild: ({ id, type, groupId, child }) => {
      return customerAPI(
        `flows/${id}/stepGroups/${type}/${groupId}/stepChildren`,
        { method: 'POST', body: child }
      );
    },
    getTrialStatus: () => customerAPI(`/trial_status`),

    /**
     * Customer API - Pins
     *
     * NOTE: These endpoints _may_ become more generic as we move towards
     *       grouping all of these as experiences.
     */
    getPins: () => customerAPI('experiences?type=persistent'),

    getPin: id => customerAPI(`experiences/${id}`),

    createPin: data =>
      customerAPI('experiences?template=persistent', {
        method: 'POST',
        body: data,
      }),

    revertPin: id =>
      customerAPI(`experiences/${id}/revert`, { method: 'POST' }),

    updatePin: (id, delta) =>
      customerAPI(`experiences/${id}`, { method: 'PATCH', body: delta }),

    publishPin: id =>
      customerAPI(`experiences/${id}/publish`, { method: 'POST' }),

    unpublishPin: id =>
      customerAPI(`experiences/${id}/unpublish`, { method: 'POST' }),

    clonePin: id => customerAPI(`experiences/${id}/clone`, { method: 'POST' }),

    deletePin: id => customerAPI(`experiences/${id}`, { method: 'DELETE' }),

    exportAnalytics: data =>
      rbfAPI(`csv`, {
        method: 'POST',
        body: { ...data, account_id: accountId },
        account: false,
      }),

    /**
     * Customer API - Experiences
     */
    getExperiences: () => customerAPI('experiences'),

    getExperience: id => customerAPI(`experiences/${id}`),

    getVersionedExperience: (id, version) =>
      customerAPI(`experiences/${id}/versions/${version}`),

    createExperience: ({ template, ...data }) =>
      customerAPI(`experiences?template=${template}`, {
        method: 'POST',
        body: data,
      }),

    updateExperience: (id, delta) =>
      customerAPI(`experiences/${id}`, { method: 'PATCH', body: delta }),

    deleteExperience: id =>
      customerAPI(`experiences/${id}`, { method: 'DELETE' }),

    cloneExperience: (id, data) =>
      customerAPI(`experiences/${id}/clone`, { method: 'POST', body: data }),

    publishExperience: id =>
      customerAPI(`experiences/${id}/publish`, { method: 'POST' }),

    revertExperience: id =>
      customerAPI(`experiences/${id}/revert`, { method: 'POST' }),

    unpublishExperience: id =>
      customerAPI(`experiences/${id}/unpublish`, { method: 'POST' }),
    archiveExperience: id =>
      customerAPI(`experiences/${id}/archive`, { method: 'POST' }),

    unarchiveExperience: id =>
      customerAPI(`experiences/${id}/unarchive`, { method: 'POST' }),

    getExperienceVersions: id => customerAPI(`experiences/${id}/versions`),

    /**
     * Customer API - Apps
     */
    getApps: () => customerAPI('apps'),

    getApp: id => customerAPI(`apps/${id}`),

    createApp: data => customerAPI('apps', { method: 'POST', body: data }),

    updateApp: (id, delta) =>
      customerAPI(`apps/${id}`, { method: 'PATCH', body: delta }),

    /**
     * Customer API - Goals
     */
    getGoals: () => customerAPI('goals'),

    /**
     * Customer API - Rules
     */
    getRules: () => customerAPI('rules'),

    getRule: id => customerAPI(`rules/${id}`),

    updateRule: (id, delta) =>
      customerAPI(`rules/${id}`, { method: 'PATCH', body: delta }),

    getSegments: () => customerAPI('segments'),
    createSegment: data =>
      customerAPI('segments', { method: 'POST', body: data }),

    /**
     * Customer API - Content Status
     */
    getContentStatus: id => customerAPI(`experiences/${id}/content-status`),

    /**
     * Customer API - Profile attribute labels
     */
    getProfileAttributeLabels: () => customerAPI('profile_attributes/labels'),

    /**
     * Customer API - Users
     */
    getUser: id => customerAPI(`users/${id}`, { account: false }),
    updateUser: (id, delta) =>
      customerAPI(`users/${id}`, {
        account: false,
        method: 'PATCH',
        body: delta,
      }),

    /**
     * Customer API - Accounts
     */
    getAccounts: userId =>
      customerAPI(`users/${userId}/accounts`, { account: false }),

    /**
     * Firebase endpoints
     */
    getAccount: () => firebaseAPI('meta'),

    updateAccount: delta =>
      firebaseAPI(`meta`, { method: 'PATCH', body: delta }),

    getAccountUsers: () => customerAPI(`users`),
    getAccountUser: id => customerAPI(`users/${id}`),

    getChecklists: () => firebaseAPI('checklists'),

    getThemes: () => firebaseAPI('styles'),

    getEventNameLabels: () => firebaseAPI(`user-events`),

    /**
     * Firebase endpoints - Tags
     */
    getTags: () => customerAPI('tags'),

    createTag: data => customerAPI('tags', { method: 'POST', body: data }),

    updateTag: (id, delta) =>
      customerAPI(`tags/${id}`, { method: 'PATCH', body: delta }),

    deleteTag: id => customerAPI(`tags/${id}`, { method: 'DELETE' }),

    /**
     * Image Service API
     */
    purgeImage: path => stepImageAPI(path),

    presignImage: path => stepImageAPI(path, { responseType: 'text' }),

    getPresignedImage: path =>
      stepImageCacheAPI(path, { account: false, responseType: 'blob' }),

    getExperienceImage: (id, stepId, state = 'DRAFT', version) => {
      const queryParams = { state };

      if (state === 'VERSIONED') {
        queryParams.versionId = version;
      }

      return imageServiceAPI(
        `experiences/${id}/steps/${stepId}?${qs.stringify(queryParams)}`,
        {
          responseType: 'blob',
        }
      );
    },

    /**
     * Listserv endpoints
     */

    getInstalledDomains: () => listservAPI('domains'),
  };
}
